import * as React from 'react';
import { cn } from '@/utils';

const toneStyles = {
	neutral: {
		container: 'border-border/60 bg-background/80 text-foreground/85',
		dot: 'bg-foreground/40',
	},
	primary: {
		container: 'border-primary/50 bg-primary/15 text-foreground/90',
		dot: 'bg-primary',
	},
	secondary: {
		container: 'border-secondary/50 bg-secondary/15 text-foreground/90',
		dot: 'bg-secondary',
	},
	accent: {
		container: 'border-accent/50 bg-accent/15 text-foreground/90',
		dot: 'bg-accent',
	},
	success: {
		container: 'border-success/50 bg-success/15 text-foreground/90',
		dot: 'bg-success',
	},
	warning: {
		container: 'border-warning/50 bg-warning/15 text-foreground/90',
		dot: 'bg-warning/80',
	},
	destructive: {
		container: 'border-destructive/50 bg-destructive/15 text-foreground/95',
		dot: 'bg-destructive',
	},
} as const;

const sizeStyles = {
	sm: 'px-2 py-0.5 text-[10px]',
	md: 'px-2.5 py-0.5 text-xs',
	lg: 'px-3 py-1 text-sm',
} as const;

export type LabelTone = keyof typeof toneStyles;
export type LabelPillSize = keyof typeof sizeStyles;

export interface LabelPillProps extends React.HTMLAttributes<HTMLSpanElement> {
	tone?: LabelTone;
	size?: LabelPillSize;
	withDot?: boolean;
	uppercase?: boolean;
}

export const LabelPill = React.forwardRef<HTMLSpanElement, LabelPillProps>(
	({ className, tone = 'neutral', size = 'sm', withDot = true, uppercase = true, children, ...props }, ref) => {
		const toneStyle = toneStyles[tone];
		const sizeStyle = sizeStyles[size];

		return (
			<span
				ref={ref}
				className={cn(
					'inline-flex select-none items-center gap-1 rounded-full border font-semibold leading-none tracking backdrop-blur-sm',
					uppercase ? 'uppercase' : '',
					sizeStyle,
					toneStyle.container,
					className,
				)}
				{...props}
			>
				{withDot ? <span aria-hidden className={cn('size-1.5 rounded-full', toneStyle.dot)} /> : null}
				{children}
			</span>
		);
	},
);

LabelPill.displayName = 'LabelPill';

export default LabelPill;
